// src/features/sheets/types/index.ts
// Type definitions for the Sheets feature

// ─────────────────────────────────────────────────────────────────────────────
// Cell Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CellPosition {
  row: number;
  col: number;
}

export interface CellFormat {
  align?: "left" | "center" | "right";
  format?: "text" | "number" | "currency" | "percent" | "compact";
  decimals?: number;
  bold?: boolean;
}

export interface CellRange {
  start: CellPosition;
  end: CellPosition;
}

// ─────────────────────────────────────────────────────────────────────────────
// Fill Drag Types
// ─────────────────────────────────────────────────────────────────────────────

export type FillDirection = "down" | "right" | "up" | "left" | null;

export interface FillDragState {
  startCell: CellPosition;
  endCell: CellPosition;
  direction: FillDirection;
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Metadata Types (for Sheets Copilot)
// ─────────────────────────────────────────────────────────────────────────────

/** The purpose/type of a sheet for AI understanding */
export type SheetPurpose =
  | "watchlist"    // Price/metric tracking for multiple assets
  | "comparison"   // Side-by-side comparison of assets/protocols
  | "analysis"     // Deep dive analysis (like acquisition analysis)
  | "portfolio"    // Position tracking with P&L
  | "research"     // Research notes with data
  | "custom";      // User-defined

/** Semantic type of a column for AI understanding */
export type ColumnSemanticType =
  | "ticker"       // Asset identifier (BTC, ETH, etc.)
  | "name"         // Full name/description
  | "price"        // Current or historical price
  | "ratio"        // Valuation ratios (P/E, MC/TVL, etc.)
  | "percentage"   // Percentage values (changes, allocation)
  | "currency"     // Dollar/fiat amounts
  | "count"        // Numeric counts (DAU, transactions)
  | "date"         // Date values
  | "text"         // Free text/notes
  | "formula"      // Calculated values
  | "live";        // Live data from LIVE() function

/** How the data in a column is sourced */
export type DataSourceType =
  | "live"         // LIVE() formula - real-time data
  | "formula"      // Calculated from other cells
  | "manual"       // User-entered static value
  | "imported";    // Imported from external source

/** Metadata about a single column for AI context */
export interface ColumnMetadata {
  header: string;
  semanticType: ColumnSemanticType;
  dataSource: DataSourceType;
  liveMetric?: string;       // If dataSource === "live", which metric
  formulaPattern?: string;   // If dataSource === "formula", the pattern
  description?: string;      // AI-readable description
}

/** Section markers for multi-table sheets */
export interface SheetSection {
  id: string;
  name: string;
  startRow: number;
  endRow: number;
  type: "header" | "data" | "summary" | "separator" | "notes";
}

/** AI-specific metadata attached to sheets */
export interface SheetAIMetadata {
  purpose: SheetPurpose;
  description?: string;
  analyzedAssets: string[];         // Tickers/assets in this sheet
  analyzedSector?: string;          // If sector-specific
  columnMetadata: ColumnMetadata[]; // Semantic info per column
  sections?: SheetSection[];        // For multi-table sheets
  lastAnalyzedAt?: string;          // When AI last analyzed
  aiGenerated?: boolean;            // Was this AI-created?
}

// ─────────────────────────────────────────────────────────────────────────────
// Sheet Types
// ─────────────────────────────────────────────────────────────────────────────

export interface Sheet {
  id: string;
  name: string;
  data: string[][];
  formats: Record<string, CellFormat>; // "row,col" -> format
  colWidths: number[];
  rows: number;
  cols: number;
  createdAt: string;
  updatedAt: string;
  aiMetadata?: SheetAIMetadata;        // AI copilot metadata
}

export interface SheetMetadata {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  rowCount: number;
  colCount: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store Types
// ─────────────────────────────────────────────────────────────────────────────

export interface SheetsState {
  sheets: Sheet[];
  activeSheetId: string | null;
  isLoading: boolean;
  isSaving: boolean;
}

export interface SheetsActions {
  // Sheet CRUD
  createSheet: (name?: string) => Sheet;
  importSheet: (
    name: string,
    data: string[][],
    formats?: Record<string, CellFormat>,
    colWidths?: number[]
  ) => Sheet;
  deleteSheet: (id: string) => void;
  duplicateSheet: (id: string) => Sheet | null;
  renameSheet: (id: string, name: string) => void;
  setActiveSheet: (id: string | null) => void;

  // Cell operations
  updateCell: (sheetId: string, row: number, col: number, value: string) => void;
  updateCells: (sheetId: string, updates: Array<{ row: number; col: number; value: string }>) => void;
  setCellFormat: (sheetId: string, row: number, col: number, format: Partial<CellFormat>) => void;

  // Structure operations
  addRow: (sheetId: string, afterRow?: number) => void;
  deleteRow: (sheetId: string, row: number) => void;
  addColumn: (sheetId: string, afterCol?: number) => void;
  deleteColumn: (sheetId: string, col: number) => void;
  setColumnWidth: (sheetId: string, col: number, width: number) => void;

  // AI metadata operations
  updateSheetMetadata: (sheetId: string, metadata: Partial<SheetAIMetadata>) => void;
}

export type SheetsStore = SheetsState & SheetsActions;

// NOTE: Component props types are exported from their respective component files
// to avoid duplication. Import from "@/features/sheets/components" if needed.

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_ROWS = 50;
export const DEFAULT_COLS = 26; // A-Z
export const DEFAULT_COL_WIDTH = 100;
export const MIN_COL_WIDTH = 50;
export const MAX_COL_WIDTH = 500;

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/** Generate a cell key string for use in format/cache lookups */
export function getCellKey(row: number, col: number): string {
  return `${row},${col}`;
}

/** Parse a cell key string back to row/col */
export function parseCellKey(key: string): CellPosition | null {
  const [row, col] = key.split(",").map(Number);
  if (row === undefined || col === undefined || isNaN(row) || isNaN(col)) {
    return null;
  }
  return { row, col };
}
